import { ActivityType } from 'course-types';
import React from 'react';
import { PlayIcon, CheckSquareIcon, BookOpenIcon, CourseResourceIcon } from '@/assets/ui/activity';
import { BookOpenCheck } from 'lucide-react';

interface ActivityIconProps {
  type: ActivityType;
  className?: string;
  border?: boolean;
  size?: 'small';
}

const ActivityIcon: React.FC<ActivityIconProps> = ({
  type,
  className = '',
  border = false,
  size = '',
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return {
          container: 'size-[36px] p-1',
          icon: 'stroke-current size-5',
        };
      default:
        return {
          container: 'size-[50px] p-[14px]',
          icon: 'stroke-current size-5',
        };
    }
  };

  const sizeClasses = getSizeClasses();

  const getBorderColor = () => {
    switch (type) {
      case 'document':
        return 'border-blue-400 bg-icon_blue_50 text-blue-500';
      case 'video':
        return 'border-purple-400 bg-icon_purple_50 text-purple-500';
      case 'quiz':
        return 'border-orange-400 bg-icon_orange_50 text-orange-500';
      default:
        return 'border-gray-400 bg-icon_gray_50 text-gray-500';
    }
  };

  const getDefaultTextColor = () => {
    switch (type) {
      case 'document':
        return 'text-blue-500';
      case 'video':
        return 'text-purple-500';
      case 'quiz':
        return 'text-orange-500';
      case 'resource':
        return 'text-gray-400';
      default:
        return 'text-gray-500';
    }
  };

  const getIcon = () => {
    let iconClasses;

    if (border) {
      iconClasses = sizeClasses.icon;
    } else {
      // Check if className contains a text color class
      const hasTextColor = className && /text-\w+/.test(className);
      const defaultTextColor = hasTextColor ? '' : getDefaultTextColor();
      iconClasses = `${sizeClasses.icon} ${className} ${defaultTextColor}`.trim();
    }

    switch (type) {
      case 'document':
        return <BookOpenIcon className={iconClasses} />;
      case 'video':
        return <PlayIcon className={iconClasses} />;
      case 'quiz':
        return <CheckSquareIcon className={iconClasses} />;
      case 'resource':
        return <CourseResourceIcon className={iconClasses} />;
      case 'assignment':
        return <BookOpenCheck className={iconClasses} />;
      default:
        return <BookOpenIcon className={iconClasses} />;
    }
  };

  if (!border) {
    return getIcon();
  }

  const iconStyle = `${className} ${sizeClasses.container} rounded-[8px] border ${getBorderColor()} flex items-center justify-center`;

  return <div className={iconStyle}>{getIcon()}</div>;
};

export default ActivityIcon;
